// War Room Planning table: every stocked ASIN with product name + purchase-planning columns,
// plus per-ASIN tags and a FLAG toggle. Fully sortable (click any header, asc/desc toggle,
// numeric-aware, nulls always sort last) and filterable (rank cutoff, size tier, verdict,
// undercut/flagged/selected toggles, tag text, search). Tag/flag writes are optimistic — this
// component keeps its own copy of `rows` (synced from the `rows` prop on refetch) so a write can
// update the UI immediately and revert in place if the API call fails.
//
// PO building: clicking anywhere on a row (except interactive elements — links, flag star, tag
// chips/input) toggles that row into a client-side `selectedAsins` set. Selection itself never
// hits the server; "Build Draft PO" opens a modal (BuildPoModal) where quantities are edited and
// only then written server-side via the bulk draft endpoint.
import React, { useEffect, useMemo, useState } from 'react';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { StarIcon as StarIconOutline, XMarkIcon, ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import AsinHistoryPanel from './AsinHistoryPanel';
import ApiClient from '../../utils/ApiClient';
import BuildPoModal from './BuildPoModal';

const ALL_VERDICTS = ['ENFORCE-MAP', 'DEFEND', 'CONTEST', 'PRICE', 'FLIP', 'SPREAD', 'HOLD', 'REVIEW'];
const ALL_TIERS = ['Standard', 'Oversize', 'ExtraLarge'];

const VERDICT_STYLES = {
    'ENFORCE-MAP': 'bg-amber-100 text-amber-800 border-amber-300',
    'DEFEND': 'bg-red-100 text-red-800 border-red-300',
    'CONTEST': 'bg-blue-100 text-blue-800 border-blue-300',
    'PRICE': 'bg-rose-100 text-rose-800 border-rose-300',
    'FLIP': 'bg-green-100 text-green-800 border-green-300',
    'SPREAD': 'bg-purple-100 text-purple-800 border-purple-300',
    'HOLD': 'bg-gray-100 text-gray-800 border-gray-300',
    'REVIEW': 'bg-slate-100 text-slate-800 border-slate-300',
};

const TIER_BADGE = {
    Standard: 'bg-green-100 text-green-800',
    Oversize: 'bg-amber-100 text-amber-800',
    ExtraLarge: 'bg-red-100 text-red-800',
};

const fmtNum = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
const fmtMoney = (v) => (v === null || v === undefined ? '—' : `$${Math.round(v).toLocaleString()}`);

const bbColor = (v) => {
    if (v == null) return '';
    if (v >= 70) return 'text-green-700';
    if (v >= 30) return 'text-amber-600';
    return 'text-red-600';
};

// UPS-ground coverage color: >=70% green, >=40% amber, <40% red.
const covColor = (v) => {
    if (v == null) return '';
    if (v >= 70) return 'text-green-700';
    if (v >= 40) return 'text-amber-600';
    return 'text-red-600';
};

// Transit-time "Speed" cell: green when we're faster than MP, red when slower, gray when either
// side has no scrape data. our_promise_days/mp_promise_days are 7-day demand-weighted averages
// (NEW FBA offers only, seller_id-matched) -- see ScorecardService#PROMISE_WINDOW_DAYS.
function speedColor(row) {
    const our = row.our_promise_days;
    const mp = row.mp_promise_days;
    if (our == null || mp == null) return 'text-gray-400';
    if (our < mp) return 'text-green-700 font-semibold';
    if (our > mp) return 'text-red-600 font-semibold';
    return 'text-gray-700';
}

function speedText(row) {
    const our = row.our_promise_days;
    const mp = row.mp_promise_days;
    if (our == null && mp == null) return '—';
    return `${our != null ? our : '—'} vs ${mp != null ? mp : '—'}`;
}

// Our share of regionally pinned buy boxes: pins_us / (pins_us + pins_mp + pins_other). Null
// (not 0) when there's no pin data at all, so it renders '—' rather than a misleading 0%.
function sug10Fc(row) {
    if ((row.fc_count ?? 0) >= 10) return 0;
    const pipeline = (row.our_units || 0) + (row.po_open_qty || 0) + (row.amz_inbound_qty || 0);
    return Math.max(0, 30 - pipeline);
}

function grossMarginPct(row) {
    // Same convention as backend margin_pct: percentage of MAP, one decimal.
    const map = Number(row.map_price), cost = Number(row.cost);
    if (!map || !cost) return null;
    return Math.round(10 * (100 * (map - cost) / map)) / 10;
}

function regBbPct(row) {
    const us = row.pins_us || 0;
    const mp = row.pins_mp || 0;
    const other = row.pins_other || 0;
    const denom = us + mp + other;
    if (denom <= 0) return null;
    return Math.round((100 * us) / denom);
}

function regBbTitle(row) {
    return `us ${row.pins_us ?? 0} / MP ${row.pins_mp ?? 0} / other ${row.pins_other ?? 0}`;
}

function parseTags(tagsStr) {
    if (!tagsStr) return [];
    return String(tagsStr).split(',').map((t) => t.trim()).filter(Boolean);
}

function uc9moPct(row) {
    if (row.uc_9mo_days === null || row.uc_9mo_days === undefined) return null;
    return Math.round((100 * row.uc_9mo_days) / 270);
}

function uc9moColor(pct) {
    if (pct == null) return '';
    if (pct >= 50) return 'text-red-600 font-semibold';
    if (pct >= 20) return 'text-amber-600 font-semibold';
    return 'text-gray-700';
}

// "Listing vel." (rank_implied_units_mo): listing-wide units/mo implied by the sales rank via the
// self-calibrated curve in ScorecardService (RANK_CURVE_A/B). invisible_mkt_ratio is the
// LOST-MARKET ratio: rank-implied listing velocity / our actual captured sales per month.
// Diagnostic only — feeds no pools, runway, or scoring; re-pricing on it is an owner decision.
const LISTING_VEL_TOOLTIP =
    'estimated listing-wide sales from rank vs the sales we actually capture — the gap is mostly '
    + 'sales lost to competitors (price undercuts, faster promises), plus any used-offer/variation sales.';
const LOST_MARKET_WARN =
    "most of this listing's sales bypass our offer — lost to competitors (undercuts, faster promises) "
    + 'or used/variation offers; investigate.';

// Amber flag when the listing appears to sell >=5x what our offer captures.
const lostMarket = (row) => row.invisible_mkt_ratio != null && Number(row.invisible_mkt_ratio) >= 5;

// ---- sorting: numeric-aware, nulls always sort last regardless of direction ----
function compareValues(aVal, bVal, dir, numeric) {
    const aNull = aVal === null || aVal === undefined || aVal === '';
    const bNull = bVal === null || bVal === undefined || bVal === '';
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    let cmp;
    if (numeric) {
        cmp = Number(aVal) - Number(bVal);
    } else {
        cmp = String(aVal).localeCompare(String(bVal));
    }
    return dir === 'asc' ? cmp : -cmp;
}

const COLUMNS = [
    { key: 'flag', label: 'Flag', numeric: true, getValue: (r) => (parseTags(r.tags).includes('FLAG') ? 1 : 0) },
    { key: 'asin', label: 'ASIN', numeric: false, getValue: (r) => r.asin },
    { key: 'title', label: 'Product name', numeric: false, getValue: (r) => r.title },
    { key: 'size_tier', label: 'Size tier', numeric: false, getValue: (r) => r.size_tier },
    { key: 'sales_rank', label: 'Rank', numeric: true, getValue: (r) => r.sales_rank },
    { key: 'mkt_units_mo', label: 'Exp/mo', numeric: true, getValue: (r) => r.mkt_units_mo },
    { key: 'rank_implied_units_mo', label: 'Listing vel.', numeric: true, getValue: (r) => r.rank_implied_units_mo,
      tooltip: LISTING_VEL_TOOLTIP },
    { key: 'our_units', label: 'Held', numeric: true, getValue: (r) => r.our_units },
    { key: 'fc_count', label: 'FCs', numeric: true, getValue: (r) => r.fc_count },
    { key: 'cov_1d_pct', label: 'Cov 1d', numeric: true, getValue: (r) => r.cov_1d_pct,
      tooltip: 'Cov 1d = % of the 50 monitored metros within 1 UPS-ground day of any stocked FC (calibrate_ups_transit matrix)' },
    { key: 'cov_2d_pct', label: 'Cov 2d', numeric: true, getValue: (r) => r.cov_2d_pct,
      tooltip: 'Cov 2d = % of the 50 monitored metros within 2 UPS-ground days of any stocked FC (calibrate_ups_transit matrix)' },
    { key: 'our_promise_days', label: 'Speed', numeric: true, getValue: (r) => r.our_promise_days,
      tooltip: '7-day demand-weighted average promised delivery, NEW FBA offers only — ours vs Marketplace Pros (days)' },
    { key: 'runway_days', label: 'Rwy', numeric: true, getValue: (r) => r.runway_days },
    { key: 'po_open_qty', label: 'PO open', numeric: true, getValue: (r) => r.po_open_qty },
    { key: 'amz_inbound_qty', label: 'AMZ inb', numeric: true, getValue: (r) => r.amz_inbound_qty },
    { key: 'mp_now_max', label: 'MP now/max', numeric: true, getValue: (r) => r.mp_units_today },
    { key: 'uc_now', label: 'UC now', numeric: true, getValue: (r) => r.breaker_pct_under },
    { key: 'uc_90d_days', label: 'UC 90d', numeric: true, getValue: (r) => r.uc_90d_days },
    { key: 'uc_180d_days', label: 'UC 180d', numeric: true, getValue: (r) => r.uc_180d_days },
    { key: 'uc_9mo_pct', label: 'UC% 9mo', numeric: true, getValue: (r) => uc9moPct(r) },
    { key: 'pv_bb_pct_28d', label: 'BB%', numeric: true, getValue: (r) => r.pv_bb_pct_28d },
    { key: 'reg_bb_pct', label: 'Reg BB%', numeric: true, getValue: (r) => regBbPct(r) },
    { key: 'natl_holder_now', label: 'BB winner', numeric: false, getValue: (r) => r.natl_holder_now },
    { key: 'monthly_net_pool', label: 'Pool $', numeric: true, getValue: (r) => r.monthly_net_pool },
    { key: 'margin_pct', label: 'Margin %', numeric: true, getValue: (r) => r.margin_pct,
      tooltip: 'Margin % = (MAP − referral fee − FBA fee − cost) ÷ MAP × 100 — net of Amazon fees, before inbound freight' },
    { key: 'gross_margin', label: 'Gross %', numeric: true, getValue: (r) => grossMarginPct(r),
      tooltip: 'Gross % = (MAP − cost) ÷ MAP × 100 — product economics before any Amazon fees' },
    { key: 'sug_10fc', label: 'Sug 10FC', numeric: true, getValue: (r) => sug10Fc(r) },
    { key: 'verdict', label: 'Verdict', numeric: true, getValue: (r) => { const i = ALL_VERDICTS.indexOf(r.verdict); return i < 0 ? null : i; } },
    { key: 'tags', label: 'Tags', numeric: false, getValue: (r) => parseTags(r.tags).filter((t) => t !== 'FLAG').join(',') },
];

function TagInput({ asin, onAdd }) {
    const [value, setValue] = useState('');

    const submit = () => {
        const v = value.trim();
        if (!v) return;
        onAdd(asin, v);
        setValue('');
    };

    return (
        <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    submit();
                }
            }}
            placeholder="+ tag"
            maxLength={40}
            className="w-16 text-xs border border-gray-200 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:w-24 transition-all"
        />
    );
}

function TagsCell({ row, onToggleTag }) {
    const tags = parseTags(row.tags).filter((t) => t !== 'FLAG');
    return (
        <div className="flex flex-wrap items-center gap-1 min-w-[140px]" onClick={(e) => e.stopPropagation()}>
            {tags.map((t) => (
                <span
                    key={t}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200"
                >
                    {t}
                    <button
                        type="button"
                        onClick={() => onToggleTag(row.asin, t, false)}
                        aria-label={`Remove tag ${t}`}
                        className="hover:text-indigo-950"
                    >
                        <XMarkIcon className="h-2.5 w-2.5" />
                    </button>
                </span>
            ))}
            <TagInput asin={row.asin} onAdd={(asin, tag) => onToggleTag(asin, tag, true)} />
        </div>
    );
}

function PlanningTable({ rows: rowsProp, sellerNames = {} }) {
    const [rows, setRows] = useState(rowsProp || []);
    const [sort, setSort] = useState({ field: 'sales_rank', dir: 'asc' });

    const [rankCutoff, setRankCutoff] = useState('500000');
    const [tierFilter, setTierFilter] = useState(new Set(ALL_TIERS));
    const [verdictFilter, setVerdictFilter] = useState(new Set(ALL_VERDICTS));
    const [notUndercutNow, setNotUndercutNow] = useState(false);
    const [flaggedOnly, setFlaggedOnly] = useState(false);
    const [selectedOnly, setSelectedOnly] = useState(false);
    const [tagText, setTagText] = useState('');
    const [search, setSearch] = useState('');

    // Client-side PO-build selection. Seeded once from any pre-existing server draft so a page
    // refresh doesn't lose an in-progress build.
    const [selectedAsins, setSelectedAsins] = useState(
        () => new Set((rowsProp || []).filter((r) => r.draft_qty != null && r.draft_qty > 0).map((r) => r.asin))
    );

    const [expanded, setExpanded] = useState(new Set());
    const [historyByAsin, setHistoryByAsin] = useState({});

    const toggleExpand = (asin) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(asin)) next.delete(asin); else next.add(asin);
            return next;
        });
        if (!historyByAsin[asin]) {
            setHistoryByAsin((prev) => ({ ...prev, [asin]: { status: 'loading', rows: [] } }));
            ApiClient.get(`/api/war-room/history/${asin}`)
                .then((response) => {
                    setHistoryByAsin((prev) => ({ ...prev, [asin]: { status: 'loaded', rows: response.data || [] } }));
                })
                .catch((err) => {
                    console.error('Error fetching ASIN history:', err);
                    setHistoryByAsin((prev) => ({ ...prev, [asin]: { status: 'error', rows: [] } }));
                });
        }
    };
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        setRows(rowsProp || []);
    }, [rowsProp]);

    const handleToggleTag = async (asin, tag, add) => {
        const normalizedTag = tag.trim().toUpperCase();
        const prevRow = rows.find((r) => r.asin === asin);
        const prevTagsStr = prevRow ? prevRow.tags : null;
        const prevList = parseTags(prevTagsStr);
        const nextList = add
            ? Array.from(new Set([...prevList, normalizedTag]))
            : prevList.filter((t) => t !== normalizedTag);
        const nextTagsStr = nextList.join(',');

        setRows((rs) => rs.map((r) => (r.asin === asin ? { ...r, tags: nextTagsStr } : r)));

        try {
            const response = await ApiClient.post('/api/war-room/tag', { asin, tag: normalizedTag, add });
            const serverTagsStr = (response.data?.tags || []).join(',');
            setRows((rs) => rs.map((r) => (r.asin === asin ? { ...r, tags: serverTagsStr } : r)));
        } catch (err) {
            console.error('Error saving War Room tag:', err);
            setRows((rs) => rs.map((r) => (r.asin === asin ? { ...r, tags: prevTagsStr } : r)));
        }
    };

    const toggleRowSelected = (asin) => {
        setSelectedAsins((prev) => {
            const next = new Set(prev);
            if (next.has(asin)) next.delete(asin); else next.add(asin);
            return next;
        });
    };

    const handleRemoveLine = (asin) => {
        setSelectedAsins((prev) => {
            const next = new Set(prev);
            next.delete(asin);
            return next;
        });
    };

    // Reflects a successful bulk save back into local row state so highlighting / re-opening the
    // modal shows the persisted quantities.
    const handleDraftSaved = (qtyByAsin) => {
        setRows((rs) => rs.map((r) => (
            Object.prototype.hasOwnProperty.call(qtyByAsin, r.asin)
                ? { ...r, draft_qty: qtyByAsin[r.asin] > 0 ? qtyByAsin[r.asin] : null }
                : r
        )));
    };

    const toggleTier = (tier) => {
        setTierFilter((prev) => {
            const next = new Set(prev);
            if (next.has(tier)) next.delete(tier); else next.add(tier);
            return next;
        });
    };

    const toggleVerdict = (verdict) => {
        setVerdictFilter((prev) => {
            const next = new Set(prev);
            if (next.has(verdict)) next.delete(verdict); else next.add(verdict);
            return next;
        });
    };

    const handleSort = (field) => {
        setSort((prev) => {
            if (prev.field === field) {
                return { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
            }
            return { field, dir: 'asc' };
        });
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const tagQ = tagText.trim().toUpperCase();
        const cutoff = rankCutoff.trim() === '' ? null : Number(rankCutoff);

        return (rows || []).filter((r) => {
            if (cutoff !== null && !Number.isNaN(cutoff)) {
                if (r.sales_rank == null || r.sales_rank > cutoff) return false;
            }
            if (r.size_tier && !tierFilter.has(r.size_tier)) return false;
            if (!r.size_tier && tierFilter.size < ALL_TIERS.length) return false;
            if (!verdictFilter.has(r.verdict)) return false;
            if (notUndercutNow && r.breaker_pct_under != null) return false;
            const tags = parseTags(r.tags);
            if (flaggedOnly && !tags.includes('FLAG')) return false;
            if (selectedOnly && !selectedAsins.has(r.asin)) return false;
            if (tagQ && !tags.some((t) => t.includes(tagQ))) return false;
            if (q) {
                const hitAsin = (r.asin || '').toLowerCase().includes(q);
                const hitSku = (r.sku || '').toLowerCase().includes(q);
                const hitTitle = (r.title || '').toLowerCase().includes(q);
                if (!hitAsin && !hitSku && !hitTitle) return false;
            }
            return true;
        });
    }, [rows, search, tagText, rankCutoff, tierFilter, verdictFilter, notUndercutNow, flaggedOnly, selectedOnly, selectedAsins]);

    const sorted = useMemo(() => {
        const col = COLUMNS.find((c) => c.key === sort.field) || COLUMNS.find((c) => c.key === 'sales_rank');
        return [...filtered].sort((a, b) => compareValues(col.getValue(a), col.getValue(b), sort.dir, col.numeric));
    }, [filtered, sort]);

    const sortIndicator = (key) => {
        if (sort.field !== key) return null;
        return sort.dir === 'asc' ? ' ▲' : ' ▼';
    };

    const selectedRows = useMemo(
        () => rows.filter((r) => selectedAsins.has(r.asin)),
        [rows, selectedAsins]
    );

    return (
        <div className="bg-white rounded-lg shadow border border-gray-200">
            {/* Filter bar */}
            <div className="flex flex-wrap items-end gap-4 p-4 border-b border-gray-200">
                <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Rank cutoff</label>
                    <input
                        type="number"
                        value={rankCutoff}
                        onChange={(e) => setRankCutoff(e.target.value)}
                        placeholder="all"
                        className="w-28 text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>

                <div>
                    <span className="block text-xs text-gray-500 mb-0.5">Size tier</span>
                    <div className="flex items-center gap-2">
                        {ALL_TIERS.map((tier) => (
                            <label key={tier} className="inline-flex items-center gap-1 text-xs text-gray-600">
                                <input
                                    type="checkbox"
                                    checked={tierFilter.has(tier)}
                                    onChange={() => toggleTier(tier)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                {tier}
                            </label>
                        ))}
                    </div>
                </div>

                <div>
                    <span className="block text-xs text-gray-500 mb-0.5">Verdict</span>
                    <div className="flex flex-wrap items-center gap-2 max-w-md">
                        {ALL_VERDICTS.map((v) => (
                            <label key={v} className="inline-flex items-center gap-1 text-xs text-gray-600">
                                <input
                                    type="checkbox"
                                    checked={verdictFilter.has(v)}
                                    onChange={() => toggleVerdict(v)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                {v}
                            </label>
                        ))}
                    </div>
                </div>

                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={notUndercutNow}
                        onChange={() => setNotUndercutNow((v) => !v)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Not undercut now
                </label>

                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={flaggedOnly}
                        onChange={() => setFlaggedOnly((v) => !v)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Flagged only
                </label>

                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={selectedOnly}
                        onChange={() => setSelectedOnly((v) => !v)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Selected only
                </label>

                <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Tag</label>
                    <input
                        type="text"
                        value={tagText}
                        onChange={(e) => setTagText(e.target.value)}
                        placeholder="Filter by tag…"
                        className="w-32 text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>

                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search ASIN, SKU or title…"
                    className="w-64 text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />

                <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    disabled={selectedAsins.size === 0}
                    className="ml-auto text-sm font-semibold px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Build Draft PO ({selectedAsins.size})
                </button>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            <th className="px-1.5 py-1.5 w-6"></th>
                            {COLUMNS.map((col) => (
                                <th
                                    key={col.key}
                                    onClick={() => handleSort(col.key)}
                                    title={col.tooltip || ''}
                                    className="px-1.5 py-1.5 cursor-pointer select-none hover:bg-gray-100 whitespace-nowrap"
                                >
                                    {col.label}
                                    {sortIndicator(col.key)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-xs">
                        {sorted.map((row) => {
                            const tags = parseTags(row.tags);
                            const flagged = tags.includes('FLAG');
                            const selected = selectedAsins.has(row.asin);
                            const isExpanded = expanded.has(row.asin);
                            const history = historyByAsin[row.asin];
                            return (
                                <React.Fragment key={row.asin}>
                                <tr
                                    onClick={() => toggleRowSelected(row.asin)}
                                    className={`align-top cursor-pointer hover:bg-gray-50 ${
                                        selected ? 'bg-blue-50 border-l-4 border-blue-500' : 'border-l-4 border-transparent'
                                    }`}
                                >
                                    <td className="px-1.5 py-1.5">
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); toggleExpand(row.asin); }}
                                            aria-label={isExpanded ? 'Collapse weekly history' : 'Expand weekly history'}
                                            className="text-gray-400 hover:text-gray-700"
                                        >
                                            {isExpanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                                        </button>
                                    </td>
                                    <td className="px-1.5 py-1.5">
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggleTag(row.asin, 'FLAG', !flagged);
                                            }}
                                            aria-label={flagged ? 'Unflag ASIN' : 'Flag ASIN'}
                                            className="text-amber-500 hover:text-amber-600"
                                        >
                                            {flagged ? (
                                                <StarIconSolid className="h-4 w-4" />
                                            ) : (
                                                <StarIconOutline className="h-4 w-4 text-gray-300" />
                                            )}
                                        </button>
                                    </td>
                                    <td className="px-1.5 py-1.5 whitespace-nowrap">
                                        <a
                                            href={`https://www.amazon.com/dp/${row.asin}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-blue-600 hover:text-blue-800 font-mono text-xs"
                                        >
                                            {row.asin}
                                        </a>
                                    </td>
                                    <td className="px-1.5 py-1.5 max-w-[13rem] truncate text-xs" title={row.title || ''}>
                                        {row.title || '—'}
                                    </td>
                                    <td className="px-1.5 py-1.5">
                                        {row.size_tier ? (
                                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${TIER_BADGE[row.size_tier] || 'bg-gray-100 text-gray-800'}`}>
                                                {row.size_tier}
                                            </span>
                                        ) : '—'}
                                    </td>
                                    <td className="px-1.5 py-1.5 text-center">{fmtNum(row.sales_rank)}</td>
                                    <td className="px-1.5 py-1.5 text-center">{fmtNum(row.mkt_units_mo)}</td>
                                    <td
                                        className={`px-1.5 py-1.5 text-center whitespace-nowrap ${lostMarket(row) ? 'bg-amber-100 text-amber-900 font-semibold' : ''}`}
                                        title={lostMarket(row) ? `${LOST_MARKET_WARN} (${row.invisible_mkt_ratio}x our captured sales)` : LISTING_VEL_TOOLTIP}
                                    >
                                        {fmtNum(row.rank_implied_units_mo)}
                                        {lostMarket(row) && (
                                            <span className="ml-1 text-[10px] font-bold text-amber-700">{Math.round(row.invisible_mkt_ratio)}x</span>
                                        )}
                                    </td>
                                    <td className="px-1.5 py-1.5 text-center">{fmtNum(row.our_units)}</td>
                                    <td className="px-1.5 py-1.5 text-center">{fmtNum(row.fc_count)}</td>
                                    <td className={`px-1.5 py-1.5 text-center font-semibold ${covColor(row.cov_1d_pct)}`}>
                                        {row.cov_1d_pct != null ? `${row.cov_1d_pct}%` : '—'}
                                    </td>
                                    <td className={`px-1.5 py-1.5 text-center font-semibold ${covColor(row.cov_2d_pct)}`}>
                                        {row.cov_2d_pct != null ? `${row.cov_2d_pct}%` : '—'}
                                    </td>
                                    <td className={`px-1.5 py-1.5 text-center whitespace-nowrap ${speedColor(row)}`} title="Ours vs Marketplace Pros, 7-day demand-weighted average promised delivery days">
                                        {speedText(row)}
                                    </td>
                                    <td className="px-1.5 py-1.5 text-center">{fmtNum(row.runway_days)}</td>
                                    {/* Owner, 2026-09-07: hover names the referencing POs (the tiny
                                        text truncates visually on wide lists; the title never does). */}
                                    <td className="px-1.5 py-1.5 text-center"
                                        title={row.po_open_numbers ? `Open PO(s): ${row.po_open_numbers}` : undefined}>
                                        <div>{fmtNum(row.po_open_qty)}</div>
                                        {row.po_open_numbers && (
                                            <div className="text-[10px] font-normal text-gray-400 whitespace-nowrap">PO {row.po_open_numbers}</div>
                                        )}
                                    </td>
                                    <td className="px-1.5 py-1.5 text-center">{fmtNum(row.amz_inbound_qty)}</td>
                                    <td className="px-1.5 py-1.5 text-center whitespace-nowrap">
                                        {row.mp_units_today ?? '—'} / {row.mp_max_90d ?? '—'}
                                    </td>
                                    <td className="px-1.5 py-1.5 whitespace-nowrap">
                                        {row.breaker_pct_under != null ? (
                                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                                                ⚠ {fmtPct(row.breaker_pct_under)}
                                            </span>
                                        ) : '—'}
                                    </td>
                                    <td className="px-1.5 py-1.5 text-center" title={row.uc_90d_max_pct != null ? `max ${row.uc_90d_max_pct}% under` : ''}>
                                        {row.uc_90d_days != null ? `${row.uc_90d_days}d` : '—'}
                                    </td>
                                    <td className="px-1.5 py-1.5 text-center" title={row.uc_180d_max_pct != null ? `max ${row.uc_180d_max_pct}% under` : ''}>
                                        {row.uc_180d_days != null ? `${row.uc_180d_days}d` : '—'}
                                    </td>
                                    <td className={`px-1.5 py-1.5 text-center ${uc9moColor(uc9moPct(row))}`}>
                                        {uc9moPct(row) != null ? `${uc9moPct(row)}%` : '—'}
                                    </td>
                                    <td className={`px-1.5 py-1.5 text-center font-semibold ${bbColor(row.pv_bb_pct_28d)}`}>{fmtPct(row.pv_bb_pct_28d)}</td>
                                    <td className={`px-1.5 py-1.5 text-center font-semibold ${bbColor(regBbPct(row))}`} title={regBbTitle(row)}>
                                        {regBbPct(row) != null ? `${regBbPct(row)}%` : '—'}
                                    </td>
                                    <td className="px-1.5 py-1.5 text-center">
                                        {(() => {
                                            const h = row.natl_holder_now;
                                            if (!h || h === 'no-check') return <span className="text-gray-400">—</span>;
                                            if (h === 'US') return <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">Us</span>;
                                            if (h === 'MP') return <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800">MP</span>;
                                            if (h === 'NONE') return <span className="text-gray-400 text-xs">none</span>;
                                            const nm = sellerNames[h];
                                            const label = nm ? (nm.length > 16 ? nm.slice(0, 15) + '…' : nm) : 'Other';
                                            return <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800" title={nm ? `${nm} (${h})` : h}>{label}</span>;
                                        })()}
                                    </td>
                                    <td className="px-1.5 py-1.5 text-center font-semibold">{fmtMoney(row.monthly_net_pool)}</td>
                                    <td className="px-1.5 py-1.5 text-center">{row.margin_pct != null ? `${row.margin_pct}%` : '—'}</td>
                                    <td className="px-1.5 py-1.5 text-center">{grossMarginPct(row) != null ? `${grossMarginPct(row)}%` : '—'}</td>
                                    <td className="px-1.5 py-1.5 text-center">{sug10Fc(row) === 0 ? '✓' : sug10Fc(row)}</td>
                                    <td className="px-1.5 py-1.5">
                                        {row.verdict ? (
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${VERDICT_STYLES[row.verdict] || VERDICT_STYLES.REVIEW}`}>
                                                {row.verdict}
                                            </span>
                                        ) : '—'}
                                    </td>
                                    <td className="px-1.5 py-1.5">
                                        <TagsCell row={row} onToggleTag={handleToggleTag} />
                                    </td>
                                </tr>
                                {isExpanded && (
                                    <tr>
                                        <td colSpan={COLUMNS.length + 1} className="px-1.5 py-1.5 bg-gray-50">
                                            <AsinHistoryPanel
                                                status={history ? history.status : 'loading'}
                                                rows={history ? history.rows : []}
                                                asin={row.asin}
                                                dwCov1dPct={row.dw_cov_1d_pct}
                                                dwCov2dPct={row.dw_cov_2d_pct}
                                                dwCov1dEffPct={row.dw_cov_1d_eff_pct}
                                                dwCov2dEffPct={row.dw_cov_2d_eff_pct}
                                            />
                                        </td>
                                    </tr>
                                )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {sorted.length === 0 && (
                <div className="p-8 text-center text-sm text-gray-500">No ASINs match the current filters.</div>
            )}

            <BuildPoModal
                open={modalOpen}
                rows={selectedRows}
                onClose={() => setModalOpen(false)}
                onRemoveLine={handleRemoveLine}
                onSaved={handleDraftSaved}
            />
        </div>
    );
}

export default PlanningTable;
